import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MedewerkerType as Role, PdfOpenenVia } from "../enums";
import { zetPdfOpenenVia } from "../services/mijnVoorkeurenService";
import { getMsalInstance, isMsalConfigured } from "../config/msalInstance";
import { loginRequest } from "../config/msalConfig";
import { UNAUTHORIZED_EVENT } from "../http";
import { AccountInfo } from "@azure/msal-browser";

const API_BASE = process.env.REACT_APP_API_URL;

// export enum Role {
//   ADMIN = 'ADMIN',
//   ARTS = 'ARTS',
//   ASSISTENT = 'ASSISTENT',
//   PM = 'PM',
// }

interface User {
  id: string;
  email: string;
  roles: Role[];
  /** B9: weergavenaam en afkorting voor het notitiestempel (uit /auth/me). */
  naam?: string;
  afkorting?: string | null;
  /** D9 (I18); ontbreekt bij een oude sessie, dan geldt BROWSER. */
  pdfOpenenVia?: PdfOpenenVia;
  /** P7: zet de webapp bij ONEDRIVE de actieve dossiers als snelkoppeling in OneDrive? */
  onedriveSnelkoppelingen?: boolean;
}

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  msalReady: boolean;
  login: (email: string, password: string) => Promise<User | null>;
  loginWithMicrosoft: () => Promise<User | null>;
  logout: () => Promise<void>;
  hasRole: (...roles: Role[]) => boolean;
  /**
   * Kijkt of er (weer) een geldige sessie is, bv. na inloggen in een ander
   * tabblad, en zet dan de gebruiker zonder de pagina te herladen. Zo gaat een
   * gepauzeerde upload verder in plaats van te verdwijnen.
   */
  vernieuwSessie: () => Promise<boolean>;
  /**
   * Bewaart de pdf-keuze (D9) en zet hem in de sessie. Optioneel in het type,
   * zodat bestaande testcontexten zonder dit veld blijven werken; de provider
   * geeft hem altijd mee, en usePdfOpenenVia valt anders terug op de server.
   */
  wijzigPdfOpenenVia?: (waarde: PdfOpenenVia) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [msalReady, setMsalReady] = useState(false);
  const [msalAccount, setMsalAccount] = useState<AccountInfo | null>(null);
  const queryClient = useQueryClient();

  // Initialize MSAL on mount
  useEffect(() => {
    if (isMsalConfigured()) {
      getMsalInstance()
        .then((msal) => {
          // Check for existing account
          const accounts = msal.getAllAccounts();
          if (accounts.length > 0) {
            setMsalAccount(accounts[0]);
          }
          setMsalReady(true);
        })
        .catch((error) => {
          console.error("MSAL initialization failed:", error);
          setMsalReady(false);
        });
    } else {
      // MSAL not configured, mark as ready anyway
      setMsalReady(true);
    }
  }, []);

  // Sessie-verloop: zodra de API 401 teruggeeft (JWT verlopen) gaan we terug
  // naar het loginscherm i.p.v. een "ingelogde" app waarin elke request faalt.
  useEffect(() => {
    const onUnauthorized = () => {
      queryClient.clear();
      setUser(null);
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [queryClient]);

  // TODO: Make sure this endpoint is protected and returns 401 if not authenticated
  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/auth/me`, {
      credentials: "include",
    })
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error("Not authenticated");
      })
      .then((data) => setUser(data.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (
    email: string,
    password: string,
  ): Promise<User | null> => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || "Login failed");
    }

    const data = await res.json();
    setUser(data.user);

    // Clear any existing cache when new user logs in
    queryClient.clear();
    return data.user;
  };

  const loginWithMicrosoft = async () => {
    if (!isMsalConfigured() || !msalReady) {
      throw new Error("Microsoft login is niet beschikbaar");
    }

    try {
      const msal = await getMsalInstance();
      // Volledige redirect i.p.v. popup: werkt in elke browser (geen
      // popup-blocker, geen hash-race in een popupvenster). De pagina verlaat
      // hier de app; completeMicrosoftRedirect() in index.tsx rondt de login
      // af zodra Microsoft ons terugstuurt.
      await msal.loginRedirect(loginRequest);
      return null;
    } catch (error: any) {
      if (error.errorCode === "interaction_in_progress") {
        throw new Error(
          "Er loopt al een inlogpoging. Ververs de pagina en probeer het opnieuw.",
        );
      }
      console.error("Microsoft login error:", error);
      throw new Error(error.errorMessage || "Microsoft login mislukt");
    }
  };

  const logout = async () => {
    // Clear internal session first (always succeeds)
    try {
      const res = await fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));

      // Attempt Microsoft logout if account exists and msAccountId is present
      if (msalAccount && data.msAccountId && isMsalConfigured()) {
        try {
          const msal = await getMsalInstance();
          await msal.logoutPopup({
            account: msalAccount,
          });
        } catch (error) {
          // MS logout failed, but continue - internal session is already cleared
          console.warn("Microsoft logout failed:", error);
        }
      }
    } catch (error) {
      // Logout request failed, but clear local state anyway
      console.error("Logout error:", error);
    }

    setUser(null);
    setMsalAccount(null);

    // CRITICAL: Clear all cached data on logout for security
    queryClient.clear();
  };

  const wijzigPdfOpenenVia = async (waarde: PdfOpenenVia) => {
    await zetPdfOpenenVia(waarde);
    setUser((huidig) => (huidig ? { ...huidig, pdfOpenenVia: waarde } : huidig));
  };

  const hasRole = (...roles: Role[]) =>
    !!user && roles.some((role) => user.roles.includes(role));

  // fetch i.p.v. http: een 401 hier is een gewone "nog niet", geen
  // sessie-verloop-melding.
  const vernieuwSessie = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        credentials: "include",
      });
      if (!res.ok) return false;
      const data = await res.json();
      if (!data?.user) return false;
      setUser(data.user);
      return true;
    } catch {
      return false;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        msalReady,
        login,
        loginWithMicrosoft,
        logout,
        hasRole,
        vernieuwSessie,
        wijzigPdfOpenenVia,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};

/**
 * De pdf-keuze (D9). Werkt ook zonder AuthProvider (losse componenttests):
 * dan geldt BROWSER en gaat een wijziging rechtstreeks naar de server.
 */
export function usePdfOpenenVia(): {
  pdfOpenenVia: PdfOpenenVia;
  wijzigPdfOpenenVia: (waarde: PdfOpenenVia) => Promise<void>;
  onedriveSnelkoppelingen: boolean;
} {
  const ctx = useContext(AuthContext);
  return {
    pdfOpenenVia: ctx?.user?.pdfOpenenVia ?? PdfOpenenVia.BROWSER,
    wijzigPdfOpenenVia: ctx?.wijzigPdfOpenenVia ?? zetPdfOpenenVia,
    onedriveSnelkoppelingen: ctx?.user?.onedriveSnelkoppelingen ?? false,
  };
}

// Hier komt de RoleGuard component

interface RoleGuardProps {
  roles: Role[];
  fallback?: ReactNode;
  children: ReactNode;
}

export const RoleGuard: React.FC<RoleGuardProps> = ({
  roles,
  fallback = null,
  children,
}) => {
  const { hasRole } = useAuth();
  return hasRole(...roles) ? <>{children}</> : <>{fallback}</>;
};
